import Vue from 'vue';
import Component from 'vue-class-component';
import { Garde, Recherche, UtilisateurExterne } from '@/services/api/types';
import { formatDate } from '@/services/dateFormatter';

@Component
export default class UserMixin extends Vue {
  public gardes: Garde[] = [];
  public filteredGardes: Garde[] = [];

  // Retourne l'objet info s'il est défini, sinon retourne un objet vide
  get utilisateurExterneInfo(): UtilisateurExterne {
    return this.$store.getters['authentication/utilisateurExterneInfo'] || {};
  }

  get loggedIn(): boolean {
    return this.$store.getters['authentication/isLoggedIn'] || false;
  }

  appliquerFiltres(filtres: Recherche): void {
    const filtreDateDebut = filtres.afficherDateDebut;
    const filtreDateFin = filtres.afficherDateFin;
    const statut = filtres.statut;
    const lot = filtres.lot;
    const secteur = filtres.secteur;
    const raisonSociale = filtres.raisonSociale;
    const rpps = filtres.rpps;
    const executant = filtres.executant;
    const finess = filtres.finess;
    const numeroAm = filtres.numeroAm;
    const departement = filtres.departement;
    this.gardes = this.$store.getters['garde/getGardes'];
    this.filteredGardes = this.gardes.filter((garde) => {
      const gardeDate = formatDate(garde.gardeRecue.date, 'YYYY-MM-DD');

      // Si la date de début est renseignée et la date de la garde est antérieure à la date saisie
      if (filtreDateDebut && new Date(gardeDate) < new Date(filtreDateDebut)) {
        return false;
      }

      // Si la date de fin est renseignée et la date de la garde est postérieure à la date saisie
      if (filtreDateFin && new Date(gardeDate) > new Date(filtreDateFin)) {
        return false;
      }

      if (lot && !garde.lotPrint.includes(lot)) {
        return false;
      }

      if (statut && garde.statutLibelleFront !== statut) {
        return false;
      }

      if (lot && !garde.lotPrint.includes(lot)) {
        return false;
      }

      if (
        finess &&
        'finess' in garde.executant &&
        !garde.executant.finess.includes(finess)
      ) {
        return false;
      }
      if (rpps && !garde.executant.denominationAvecNumero.includes(rpps)) {
        return false;
      }
      if (
        executant &&
        !this.normalizeString(garde.executant.denominationAvecNumero).includes(
          this.normalizeString(executant)
        )
      ) {
        return false;
      }
      //TODO revoir bug 09/01 sur recherche AM. Exempel LHOSTMOT RPPS 10002829090 +> informationExecutantPourDelegataire RENVOI RPPS
      if (numeroAm) {
        const infoExecutant = this.utilisateurExterneInfo.isDelegataire
          ? garde.executant.numeroAm
          : garde.executant.informationExecutant;

        if (
          !this.normalizeString(infoExecutant).includes(
            this.normalizeString(numeroAm)
          )
        ) {
          return false;
        }
      }
      if (
        raisonSociale &&
        !this.normalizeString(garde.executant.denominationAvecNumero).includes(
          this.normalizeString(raisonSociale)
        )
      ) {
        return false;
      }

      if (
        departement &&
        garde.gardeRecue.lot.departement.libelleComplet !== departement
      ) {
        return false;
      }
      if (
        secteur &&
        !this.normalizeString(garde.gardeRecue.secteur).includes(
          this.normalizeString(secteur)
        )
      ) {
        return false;
      }

      return true;
    });
  }

  normalizeString(str: string): string {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  getUniqueValues(attribute: string): { text: string; value: string }[] {
    if (this.gardes) {
      const valuesMap = this.gardes.map((garde) =>
        this.deepValue(garde, attribute)
      );

      const uniqueValuesSet = new Set<string>();

      valuesMap.forEach((value) => {
        if (typeof value === 'string') {
          uniqueValuesSet.add(value);
        }
      });

      return Array.from(uniqueValuesSet).map((text) => ({ text, value: text }));
    }
    return [];
  }

  deepValue(
    obj: Record<string, any>,
    path: string
  ): Record<string, any> | null {
    const segments = path.split('.');
    for (const segment of segments) {
      if (obj && obj.hasOwnProperty(segment)) {
        obj = obj[segment];
      } else {
        return null;
      }
    }
    return obj;
  }

  uniqueDays(data: any[]): string[] {
    // Extrait les jours
    const tableauJours: string[] = data.map((value) =>
      formatDate(value.dateHeure)
    );

    // dédoublonne la liste
    const uniqueDatesSet: Set<string> = new Set(tableauJours);

    // Convertit le Set en tableau
    const uniqueDatesArray: string[] = Array.from(uniqueDatesSet);

    return uniqueDatesArray;
  }

  validatePassword(password: string): boolean | string {
    // Au moins 8 caractères
    const minLength = password.length >= 8;
    // Au moins une lettre minuscule
    const hasLowercaseLetter = /[a-z]/.test(password);
    // Au moins une lettre majuscule
    const hasUppercaseLetter = /[A-Z]/.test(password);
    // Au moins un chiffre
    const hasNumber = /\d/.test(password);
    // Au moins un caractère spécial
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>[\]_+=\-\\]/.test(password);

    if (
      minLength &&
      hasLowercaseLetter &&
      hasUppercaseLetter &&
      hasNumber &&
      hasSpecialChar
    ) {
      return true;
    }
    return 'Votre mot de passe doit comporter au minimum 8 caractères, parmi lesquels au moins 1 majuscule, 1 minuscule, 1 chiffre et 1 caractère spécial.';
  }

  stringMatch(
    value: string,
    valueTwo: string,
    messageErreur: string
  ): string | boolean {
    if (value !== valueTwo) {
      return messageErreur;
    }
    return true;
  }

  goToPath(path: string): void {
    this.$router.push(path);
  }
}
